import { useMemo, useState } from 'react'
import {
  Card,
  DataTable,
  FilterBar,
  PageHeader,
  Select,
  StatCard,
  FILTER_KOSONG,
  ambilBaris,
  angkaBaris,
  cariAngka,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { formatRupiah, formatTanggal, tanggalHariIni, awalTahunIni } from '@/lib/format'
import { useApi } from '@/lib/useApi'
import { useOpsiAkun, useOpsiUnit } from '@/lib/opsi'

export default function BukuBesar() {
  const opsiUnit = useOpsiUnit()
  const akun = useOpsiAkun()
  const [filter, setFilter] = useState<NilaiFilter>({
    ...FILTER_KOSONG,
    dari: awalTahunIni(),
    sampai: tanggalHariIni(),
  })
  const [accountId, setAccountId] = useState('')

  const buku = useApi<unknown>(accountId ? '/ledger' : null, {
    accountId: accountId || undefined,
    dari: filter.dari || undefined,
    sampai: filter.sampai || undefined,
    unitId: filter.unitId || undefined,
  })

  const baris = useMemo(
    () => ambilBaris(buku.data, ['lines', 'mutasi', 'entries', 'items', 'baris', 'data', 'rows']),
    [buku.data],
  )

  const saldoAwal = cariAngka(buku.data, ['saldoAwal', 'openingBalance', 'awal', 'saldoAwalPeriode']) ?? 0
  const totalDebit = baris.reduce((a, b) => a + angkaBaris(b, ['debit', 'totalDebit']), 0)
  const totalKredit = baris.reduce((a, b) => a + angkaBaris(b, ['credit', 'kredit', 'totalCredit']), 0)
  const saldoAkhirPayload = cariAngka(buku.data, ['saldoAkhir', 'closingBalance', 'akhir', 'saldo'])
  const saldoAkhir = saldoAkhirPayload ?? saldoAwal + totalDebit - totalKredit

  // Hitung saldo berjalan di klien bila API tidak menyediakannya.
  const denganBerjalan = useMemo(() => {
    let berjalan = saldoAwal
    return baris.map((b) => {
      const debit = angkaBaris(b, ['debit'])
      const kredit = angkaBaris(b, ['credit', 'kredit'])
      berjalan += debit - kredit
      const saldoApi = angkaBaris(b, ['saldo', 'runningBalance', 'balance', 'saldoBerjalan'])
      const punyaSaldo = ['saldo', 'runningBalance', 'balance', 'saldoBerjalan'].some(
        (k) => b[k] !== undefined && b[k] !== null,
      )
      return { ...b, _debit: debit, _kredit: kredit, _saldo: punyaSaldo ? saldoApi : berjalan, _berjalan: berjalan } as Record<
        string,
        unknown
      >
    })
  }, [baris, saldoAwal])

  const kolom: KolomTabel<Record<string, unknown>>[] = [
    {
      kunci: 'tanggal',
      judul: 'Tanggal',
      nilaiSort: (b) => String(b.transactionDate ?? b.journalDate ?? b.tanggal ?? ''),
      render: (b) => formatTanggal(String(b.transactionDate ?? b.journalDate ?? b.tanggal ?? '')),
    },
    {
      kunci: 'nomor',
      judul: 'Nomor',
      render: (b) => String(b.journalNumber ?? b.referenceNumber ?? b.nomor ?? b.sourceNumber ?? '—'),
    },
    {
      kunci: 'uraian',
      judul: 'Uraian',
      render: (b) => String(b.description ?? b.uraian ?? b.keterangan ?? '—'),
    },
    {
      kunci: '_debit',
      judul: 'Debit',
      rata: 'kanan',
      nilaiSort: (b) => Number(b._debit ?? 0),
      render: (b) => (Number(b._debit) ? formatRupiah(Number(b._debit)) : <span className="text-netral-300">—</span>),
    },
    {
      kunci: '_kredit',
      judul: 'Kredit',
      rata: 'kanan',
      nilaiSort: (b) => Number(b._kredit ?? 0),
      render: (b) => (Number(b._kredit) ? formatRupiah(Number(b._kredit)) : <span className="text-netral-300">—</span>),
    },
    {
      kunci: '_saldo',
      judul: 'Saldo Berjalan',
      rata: 'kanan',
      nilaiSort: (b) => Number(b._saldo ?? 0),
      render: (b) => formatRupiah(Number(b._saldo ?? 0)),
    },
  ]

  const akunTerpilih = akun.daftar.find((a) => a.id === accountId)

  return (
    <div>
      <PageHeader
        judul="Buku Besar"
        deskripsi="Mutasi satu akun pada rentang tanggal tertentu, lengkap dengan saldo awal, saldo berjalan, dan saldo akhir."
        ekspor={{
          path: '/reports/general-ledger',
          namaDasar: `buku-besar-${akunTerpilih?.code ?? 'akun'}`,
          query: {
            accountId: accountId || undefined,
            dari: filter.dari || undefined,
            sampai: filter.sampai || undefined,
            unitId: filter.unitId || undefined,
          },
        }}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: true, sampai: true, fundId: false, program: false, kategori: false }}
        ekstra={
          <Select
            label="Akun"
            placeholder={akun.memuat ? 'Memuat akun…' : 'Pilih akun'}
            opsi={akun.opsi}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="min-w-64"
            bantuan={akun.galat ? `Gagal memuat akun: ${akun.galat}` : undefined}
          />
        }
      />

      {!accountId ? (
        <Card>
          <p className="py-6 text-center text-sm text-netral-500">
            Pilih akun terlebih dahulu untuk menampilkan buku besar.
          </p>
        </Card>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Saldo Awal" nilai={saldoAwal} formatNilai={(v) => formatRupiah(Number(v ?? 0))} />
            <StatCard label="Total Debit" nilai={totalDebit} formatNilai={(v) => formatRupiah(Number(v ?? 0))} />
            <StatCard label="Total Kredit" nilai={totalKredit} formatNilai={(v) => formatRupiah(Number(v ?? 0))} />
            <StatCard
              label="Saldo Akhir"
              nilai={saldoAkhir}
              nada={saldoAkhir < 0 ? 'merah' : 'hijau'}
              formatNilai={(v) => formatRupiah(Number(v ?? 0))}
            />
          </div>

          {akunTerpilih ? (
            <p className="mb-3 text-sm text-netral-600">
              Akun: <strong>{akunTerpilih.code} — {akunTerpilih.name}</strong> · Saldo normal{' '}
              {akunTerpilih.normalBalance === 'DEBIT' ? 'Debit' : 'Kredit'}
            </p>
          ) : null}

          <DataTable
            kolom={kolom}
            data={denganBerjalan}
            kunciBaris={(b, i) => String(b.id ?? `baris-${i}`)}
            memuat={buku.memuat}
            galat={buku.galat}
            kodeGalat={buku.kodeGalat}
            onCoba={buku.muat}
            pesanKosong="Tidak ada mutasi pada rentang tanggal ini."
          />

          <div className="mt-3 flex items-center justify-end gap-4 rounded-xl border border-netral-200 bg-white px-4 py-3 text-sm">
            <span className="text-netral-500">Saldo awal {formatRupiah(saldoAwal)}</span>
            <span className="text-netral-400">+</span>
            <span className="text-netral-500">Mutasi {formatRupiah(totalDebit - totalKredit)}</span>
            <span className="text-netral-400">=</span>
            <strong className="angka-tabular text-netral-900">Saldo akhir {formatRupiah(saldoAkhir)}</strong>
          </div>
        </>
      )}
    </div>
  )
}
